// Globals
import { debounce } from 'lodash';
import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Components
import { Form, FormItem, Modal } from '@/components';
import { StyledButton, StyledButtons, TextEditorContainer } from './TextEditor.styles';
import { CheckIcon } from '@/icons';
import { Input } from '@/ui';

// Store
import { useAppState } from '@/store';

// Modules
import { useUploadFile } from '@/modules/Upload/hooks/useFileUpload';
import { selectChannelSiteUrl } from '@/modules/Channels';

// Utils
import { getNormalizedHostname } from '@/utils';

const ALL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'code',
  'image',
];

interface OnChangeHandler {
  (html: string, plainText: string): void;
}

type Props = {
  value?: string;
  placeholder?: string;
  onChange?: OnChangeHandler;
  enableImageUpload?: boolean;
  disableLinksAndImages?: boolean;
  readOnly?: boolean;
  'aria-invalid'?: boolean;
};

export const TextEditor = React.forwardRef<ReactQuill | null, Props>(
  (
    {
      value,
      onChange,
      placeholder,
      enableImageUpload = false,
      disableLinksAndImages = false,
      readOnly,
      'aria-invalid': ariaInvalid,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const prevValue = useRef<string>('');
    const { uploadFile } = useUploadFile();
    const quillRef = useRef<ReactQuill | null>(null);
    const activeChannelSiteUrl = useAppState(selectChannelSiteUrl);

    const [selectedRange, setSelectedRange] = useState<{
      range: number;
      url: string;
    } | null>(null);

    const debouncedUpdate = useRef(
      debounce((html: string, plainText: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;

        const links = div.querySelectorAll('a');

        links.forEach((link) => {
          try {
            const linkUrl = new URL(link.href, window.location.origin);
            const siteUrl = activeChannelSiteUrl ? new URL(activeChannelSiteUrl, window.location.origin) : null;

            const linkHostname = getNormalizedHostname(linkUrl.hostname);
            const siteHostname = getNormalizedHostname(siteUrl?.hostname || '');

            const isExternalLink = !activeChannelSiteUrl || linkHostname !== siteHostname;
            if (isExternalLink) {
              link.setAttribute('rel', 'nofollow');
            } else {
              link.removeAttribute('rel');
            }
          } catch (error) {
            link.setAttribute('rel', 'nofollow');
          }
          link.setAttribute('target', '_blank');
        });

        if (div.innerHTML && onChange) {
          prevValue.current = div.innerHTML;
          const isFilledHtml = plainText?.trim().length || prevValue.current.includes('img');

          onChange(isFilledHtml ? div.innerHTML : '', plainText);
        }
      }, 300),
    ).current;

    const handleChange = (_: string, delta: any, __: string, editor: any) => {
      const plainText = editor.getText();
      const html = editor.getHTML();

      if (html !== prevValue.current && onChange) {
        prevValue.current = html;
        debouncedUpdate(html, plainText);
      }
    };

    useImperativeHandle(ref, () => quillRef.current!);

    const handleImageUpload = useCallback(
      async (file: File) => {
        try {
          const response = await uploadFile({ file });
          const imageUrl = response.url;

          const editor = quillRef?.current?.getEditor();
          const range = editor?.getSelection();
          if (range) {
            setSelectedRange({ range: range.index, url: imageUrl });
          }
        } catch (error) {
          console.error('Image upload failed:', error);
        }
      },
      [uploadFile],
    );

    const handleImageInsert = useCallback(() => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
        if (input.files && input.files[0]) {
          await handleImageUpload(input.files[0]);
        }
      };
    }, [handleImageUpload]);

    const formats = useMemo(
      () =>
        disableLinksAndImages ? ALL_FORMATS.filter((format) => format !== 'link' && format !== 'image') : ALL_FORMATS,
      [disableLinksAndImages],
    );

    const modules = useMemo(() => {
      const baseToolbar = [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        disableLinksAndImages ? ['code'] : ['link', 'code'],
        ['clean'],
      ];

      if (enableImageUpload && !disableLinksAndImages) {
        baseToolbar.push(['image']);
      }

      const handlers =
        enableImageUpload && !disableLinksAndImages
          ? {
              image: handleImageInsert,
            }
          : {};

      return {
        toolbar: {
          container: baseToolbar,
          handlers,
        },
      };
    }, [enableImageUpload, disableLinksAndImages, handleImageInsert]);

    const onFormFinish = (values: any) => {
      const editor = quillRef?.current?.getEditor();
      if (!selectedRange || !editor) return;
      setSelectedRange(null);
      const img = document.createElement('img');
      img.src = selectedRange.url;
      img.alt = values?.image;
      editor.clipboard.dangerouslyPasteHTML(selectedRange.range, img.outerHTML);
    };

    return (
      <TextEditorContainer aria-invalid={ariaInvalid}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          modules={modules}
          formats={formats}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
        />
        <Modal title={t('insert_image_alt_title')} visible={!!selectedRange} onCancel={() => setSelectedRange(null)}>
          <Form onFinish={onFormFinish}>
            <FormItem name="image">
              <Input />
            </FormItem>
            <StyledButtons>
              <StyledButton type="primary" htmlType="submit" icon={<CheckIcon />}>
                {t('save')}
              </StyledButton>
            </StyledButtons>
          </Form>
        </Modal>
      </TextEditorContainer>
    );
  },
);

TextEditor.displayName = 'CustomTextEditor';
